import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-0f2869f0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "drawer-content" }
const _hoisted_2 = { class: "installed-detail-actions" }
const _hoisted_3 = { class: "selection-bar cm-ui-mb15" }
const _hoisted_4 = { class: "pagination-select-count" }
const _hoisted_5 = { class: "dialog-footer" }

import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import { ElMessage } from 'element-plus'
import type { TableOpt } from '@/components/cmTable/types'
import type { ClientInfo } from '../../client-view.service'
import { formatTimeStamp } from '@/utils/time'
import { listCol } from '@/utils/list-columns-i18n'
import {
  InstalledDetailService,
  ListQueryParams,
  SoftwareInfo,
} from './installed-detail.service'
import { splitSoftHashKey } from "@/utils/tool"

const MAX_SOFT_SELECTION = 4


export default /*#__PURE__*/_defineComponent({
  __name: 'installed-detail',
  props: {
    groupId: { default: null },
    clientInfo: { default: () => ({}) as ClientInfo },
    isTerminalInfo: { type: Boolean, default: false },
    hasPermission: { type: Boolean, default: true }
  },
  setup(__props: any) {

const props = __props

const hasPermission = computed(() => props.hasPermission)

const { t, locale } = useI18n()
const injector = useReflectiveInjector([InstalledDetailService])
const service = injector.get(InstalledDetailService)

const tableRef = ref<{
  getList: () => void
  tableRef?: { toggleRowSelection?: (row: unknown, selected?: boolean) => void; store?: { states?: { data?: { value?: SoftwareInfo[] } } } }
  total?: number
} | null>(null)
const { refreshList } = useCmTableListRefresh(tableRef)

const form = reactive({
  device_id: props.clientInfo?.device_id ?? '',
  key_word: '',
  /** 为 true 时仅分页展示当前已选软件（本地切片），不请求列表接口 */
  useMemory: false,
})

const data = reactive({
  uninstallType: 0 as 0 | 1,
  uninstallDialogVisible: false,
})

const selectedRows = ref<SoftwareInfo[]>([])
const syncingSelection = ref(false)

watch(
  () => props.clientInfo?.device_id,
  (id) => {
    form.device_id = id ?? ''
    selectedRows.value = []
    void refreshList()
  }
)

watch(
  () => [tableRef.value?.total, form.useMemory],
  () => {
    nextTick(() => syncTableSelection())
  }
)

const pageOption = { pageNumName: 'page', pageSizeName: 'limit' }

function rowKey (row: SoftwareInfo) {
  return row.soft_hash_key
}

function isRowSelected (row: SoftwareInfo) {
  return selectedRows.value.some((r) => r.soft_hash_key === row.soft_hash_key)
}

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    selection: true,
    selectColumnOpt: { width: 50 },
    selectable: (row: SoftwareInfo) => {
      if (form.useMemory) return true
      return selectedRows.value.length < MAX_SOFT_SELECTION || isRowSelected(row)
    },
    columns: [
      {
        prop: 'display_name',
        label: listCol(t, 'softwareName'),
        columnOpt: { minWidth: 160 },
      },
      {
        prop: 'version',
        label: listCol(t, 'softVersion'),
        columnOpt: { width: 120 },
      },
      {
        prop: 'install_time',
        label: t('genuineSoftwareUsageColumns.installTime'),
        columnOpt: { width: 168 },
        formatter: (_row: any, value: number) => formatTimeStamp(value, 'YYYY-MM-DD') || '--',
      },
    ],
    rowKey: 'soft_hash_key',
    showActionBar: false,
    showSearch: false,
    showRightToolbar: false,
    path: 'list',
    totalPath: 'total',
    usePagination: false,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    height: '100%',
  }
})

const tableActOpt = { columnOpt: { width: 0 }, actionButtons: () => [] }

const filterFormColumns = computed(() => {
  void locale.value
  return {}
})

const totalCount = computed(() => tableRef.value?.total ?? 0)
const selectCount = computed(() => selectedRows.value.length)
const uninstallDisabled = computed(() => selectCount.value <= 0)

async function getListApi(params: Record<string, unknown>) {
  const page = Number(params.page ?? 1)
  const limit = Number(params.limit ?? 20)

  if (form.useMemory) {
    const all = selectedRows.value
    const start = (page - 1) * limit
    const list = all.slice(start, start + limit)
    return { total: all.length, list }
  }

  const res = await service.getSoftwareList({
    device_id: form.device_id,
    key_word: String(params.key_word ?? form.key_word ?? ''),
    page,
    limit,
  })
  return { total: res.total ?? 0, list: res.list ?? [] }
}

function getCmTableBodyRows (): SoftwareInfo[] {
  const inner = tableRef.value?.tableRef as
    | { store?: { states?: { data?: { value?: SoftwareInfo[] } | SoftwareInfo[] } } }
    | undefined
  const raw = inner?.store?.states?.data
  const arr = raw && typeof raw === 'object' && 'value' in raw ? raw.value : raw
  return Array.isArray(arr) ? arr : []
}

function syncTableSelection() {
  const el = tableRef.value?.tableRef
  const tableData = getCmTableBodyRows()
  if (!el?.toggleRowSelection || !tableData.length) return
  const keys = new Set(selectedRows.value.map((r) => r.soft_hash_key))
  syncingSelection.value = true
  tableData.forEach((row) => {
    el.toggleRowSelection(row, keys.has(rowKey(row)))
  })
  nextTick(() => {
    syncingSelection.value = false
  })
}

function onChangeSelected(selection: SoftwareInfo[]) {
  if (syncingSelection.value) return
  selectedRows.value = selection.map((r) => ({ ...r }))
}

async function handleSearch(value: string) {
  form.key_word = value
  form.useMemory = false
  await refreshList()
}

async function handleQuery() {
  await refreshList()
}

function handleReset() {
  form.key_word = ''
  form.useMemory = false
  void refreshList()
}

function getListByMemory() {
  form.useMemory = !form.useMemory
  void refreshList()
}

function download(type: 0 | 1) {
  const name = props.clientInfo?.computer_name ?? ''
  const q: ListQueryParams = {
    device_id: form.device_id,
    key_word: form.key_word,
    limit: 0,
    page: 0,
  }
  service.download(type, q, t('softwareManage.exportTerminalClientInstalledDetail', { name }))
}

function showUninstallDialog() {
  data.uninstallDialogVisible = true
  data.uninstallType = 0
}

function resetRadio() {
  data.uninstallType = 0
  data.uninstallDialogVisible = false
}

function uninstallSoftware() {
  data.uninstallDialogVisible = false
  const soft_hash_keys = selectedRows.value.map((item) => splitSoftHashKey(item.soft_hash_key))
  const count = soft_hash_keys.length
  const device_id = props.clientInfo?.device_id ?? ''
  service
    .uninstallSoftware({
      uninstall_type: data.uninstallType,
      device_id,
      soft_hash_keys: soft_hash_keys.flat(5),
    })
    .then(() => {
      ElMessage.success(t('genuineSoftwareUsage.uninstallSuccessTip', { count }))
      selectedRows.value = []
      void refreshList()
    })
    .catch(() => {
      ElMessage.error(t('softwareManage.uninstallClientTerminalSoftFail', { count }))
    })
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_BlueTipsIcon = _resolveComponent("BlueTipsIcon")!
  const _component_el_radio = _resolveComponent("el-radio")!
  const _component_el_radio_group = _resolveComponent("el-radio-group")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      modelValue: form,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form) = $event)),
      "table-opt": tableOpt.value,
      "table-act-opt": tableActOpt,
      api: getListApi,
      "page-option": pageOption,
      "has-permission": hasPermission.value,
      onChangeSelected: onChangeSelected
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          modelValue: form,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form) = $event)),
          class: "installed-detail-filter-bar",
          "form-columns": filterFormColumns.value,
          disabled: !hasPermission.value,
          "show-search": true,
          "search-placeholder": _ctx.$t('softwareManage.searchPlaceholderSoft'),
          "popover-threshold": 0,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          actions: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_el_button, {
                type: "primary",
                size: "small",
                disabled: uninstallDisabled.value,
                onClick: showUninstallDialog
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('genuineSoftwareUsage.uninstall')), 1)
                ]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(ExportDropdown, {
                "has-permission": hasPermission.value,
                onExport: download
              }, null, 8, ["has-permission"])
            ])
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_BlueTipsIcon, { class: "cm-ui-ml15 cm-ui-mr10" }),
          _createElementVNode("p", _hoisted_4, [
            _createTextVNode(_toDisplayString(_ctx.$t('genuineSoftwareUsage.selectedSoftPrefix')) + " ", 1),
            _createElementVNode("a", {
              class: _normalizeClass([!form.useMemory ? 'req-primary' : 'req-default', !selectCount.value ? 'memory-count--disabled' : '']),
              onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (selectCount.value && getListByMemory()), ["prevent"]))
            }, _toDisplayString(selectCount.value), 3),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('genuineSoftwareUsage.selectedSoftSuffix')), 1)
          ])
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "table-opt", "has-permission"]),
    _createVNode(_component_el_dialog, {
      modelValue: data.uninstallDialogVisible,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((data.uninstallDialogVisible) = $event)),
      title: _ctx.$t('genuineSoftwareUsage.uninstallDialogTitle'),
      width: "30%",
      "destroy-on-close": "",
      "before-close": resetRadio
    }, {
      footer: _withCtx(() => [
        _createElementVNode("span", _hoisted_5, [
          _createVNode(_component_el_button, {
            size: "small",
            type: "primary",
            onClick: uninstallSoftware
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.confirm')), 1)
            ]),
            _: 1
          })
        ])
      ]),
      default: _withCtx(() => [
        _createVNode(_component_el_radio_group, {
          modelValue: data.uninstallType,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((data.uninstallType) = $event))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_radio, { label: 0 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('genuineSoftwareUsage.uninstallTypeNotify')), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_el_radio, { label: 1 }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('genuineSoftwareUsage.uninstallTypeSilent')), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})